/**
 * Armada Teaser Content — Paginated carousel with autoplay progress + swipe.
 *
 * Desktop: slides stacked, crossfade via opacity. Autoplay with progress bar.
 * Mobile:  stacked fade transition with swipe + controls.
 * Swipe:   touch gestures + trackpad horizontal scroll to navigate slides.
 */
if (!customElements.get('armada-teaser-content')) {
  class ArmadaTeaserContent extends HTMLElement {
    constructor() {
      super();
      this._onIndicatorClick = this._onIndicatorClick.bind(this);
      this._onPrevClick = this._onPrevClick.bind(this);
      this._onNextClick = this._onNextClick.bind(this);
      this._onMediaChange = this._onMediaChange.bind(this);
      this._advance = this._advance.bind(this);
    }

    connectedCallback() {
      this._track = this.querySelector('[data-slides-track]');
      this._slides = this.querySelectorAll('[data-slide]');
      this._indicators = this.querySelectorAll('[data-slide-btn]');
      this._announcement = this.querySelector('[data-teaser-announcement]');
      this._slideLabelTemplate = this.dataset.slideLabelTemplate || 'Slide __INDEX__ of __TOTAL__';
      this._pauseAutoplayLabel = this.dataset.pauseAutoplayLabel || 'Pause gallery autoplay';
      this._resumeAutoplayLabel = this.dataset.resumeAutoplayLabel || 'Resume gallery autoplay';
      this._prevButtons = this.querySelectorAll('[data-prev-btn]');
      this._nextButtons = this.querySelectorAll('[data-next-btn]');
      this._current = 0;
      this._mql = window.matchMedia('(min-width: 750px)');
      this._autoplayTimer = null;
      this._duration = parseInt(this.dataset.autoplayDuration, 10) || 3;
      this._paused = true;
      this._paginationPaused = false;
      this._inViewport = false;

      if (this._slides.length < 2) return;

      this._indicators.forEach((btn) => {
        btn.addEventListener('click', this._onIndicatorClick);
      });
      this._prevButtons.forEach((button) => {
        button.addEventListener('click', this._onPrevClick);
      });
      this._nextButtons.forEach((button) => {
        button.addEventListener('click', this._onNextClick);
      });

      this._mql.addEventListener('change', this._onMediaChange);
      this._onMediaChange();

      this._bindSwipe();

      this.addEventListener('mouseenter', () => this._resumeAutoplay());
      this.addEventListener('mouseleave', () => this._pauseAutoplay());

      this._observeViewport();
    }

    disconnectedCallback() {
      this._indicators.forEach((btn) => {
        btn.removeEventListener('click', this._onIndicatorClick);
      });
      this._prevButtons.forEach((button) => {
        button.removeEventListener('click', this._onPrevClick);
      });
      this._nextButtons.forEach((button) => {
        button.removeEventListener('click', this._onNextClick);
      });

      if (this._mql) {
        this._mql.removeEventListener('change', this._onMediaChange);
      }

      if (this._viewportObserver) {
        this._viewportObserver.disconnect();
      }

      this._stopAutoplay();
    }

    /* ------------------------------------------------------------------
       Viewport observation — autoplay only while section is visible
       ------------------------------------------------------------------ */
    _observeViewport() {
      this._viewportObserver = new IntersectionObserver(
        ([entry]) => {
          this._inViewport = entry.isIntersecting;
          if (this._canAutoplay()) {
            this._restartAutoplay();
          } else {
            this._stopAutoplay();
          }
        },
        { threshold: 0.3 }
      );
      this._viewportObserver.observe(this);
    }

    /* Desktop: require hover + viewport. Mobile: viewport only. */
    _canAutoplay() {
      if (!this._inViewport) return false;
      if (this._paginationPaused) return false;
      return this._mql.matches ? !this._paused : true;
    }

    /* ------------------------------------------------------------------
       Responsive mode switching
       ------------------------------------------------------------------ */
    _onMediaChange() {
      if (this._mql.matches) {
        this._initDesktop();
      } else {
        this._initMobile();
      }

      if (this._canAutoplay()) {
        this._setActiveFillAnimationState('running');
        this._restartAutoplay();
      } else {
        this._stopAutoplay();
      }
    }

    _initDesktop() {
      this._goTo(this._current);
    }

    _initMobile() {
      this._goTo(this._current);
    }

    /* ------------------------------------------------------------------
       Navigation
       ------------------------------------------------------------------ */
    _onIndicatorClick(e) {
      const index = parseInt(e.currentTarget.dataset.slideBtn, 10);
      if (index === this._current) {
        this._togglePaginationAutoplay();
        return;
      }

      if (this._mql.matches) {
        this._goTo(index);
      } else {
        this._goTo(index);
      }
      if (this._canAutoplay()) {
        this._restartAutoplay();
      }
    }

    _togglePaginationAutoplay() {
      if (this._paginationPaused) {
        this._paginationPaused = false;
        this._setActiveFillAnimationState('running');
        this._updateIndicatorAccessibility();
        this._restartAutoplay();
        return;
      }

      this._paginationPaused = true;
      this._stopAutoplay();
      this._setActiveFillAnimationState('paused');
      this._updateIndicatorAccessibility();
    }

    _onPrevClick() {
      this._navigateBy(-1);
    }

    _onNextClick() {
      this._navigateBy(1);
    }

    _goTo(index) {
      this._current = index;

      this._slides.forEach((slide) => {
        slide.classList.toggle('is-active', parseInt(slide.dataset.slide, 10) === index);
      });

      this._updateIndicators(index);
      this._updateArrowState(index);
      this._announce(index);
    }

    _navigateBy(direction) {
      const next = this._current + direction;
      if (next < 0 || next >= this._slides.length) return;

      if (this._mql.matches) {
        this._goTo(next);
      } else {
        this._goTo(next);
      }
      this._restartAutoplay();
    }

    _advance() {
      const next = (this._current + 1) % this._slides.length;
      if (this._mql.matches) {
        this._goTo(next);
      } else {
        this._goTo(next);
      }
      this._restartAutoplay();
    }

    /* ------------------------------------------------------------------
       Indicators & progress
       ------------------------------------------------------------------ */
    _updateIndicators(index) {
      this._indicators.forEach((btn) => {
        const slideIndex = parseInt(btn.dataset.slideBtn, 10);
        const isActive = slideIndex === index;
        const isPast = slideIndex < index;
        btn.classList.toggle('is-active', isActive);
        btn.classList.toggle('is-past', isPast);
        btn.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      this._indicators.forEach((btn) => {
        if (!btn.classList.contains('is-active')) return;
        const fill = btn.querySelector('.armada-teaser-content__track-fill');
        if (!fill) return;
        fill.style.animation = 'none';
        fill.offsetHeight;
        fill.style.animation = '';
      });

      this._updateIndicatorAccessibility();
    }

    _updateIndicatorAccessibility() {
      this._indicators.forEach((btn) => {
        const slideIndex = parseInt(btn.dataset.slideBtn, 10);
        const isActive = slideIndex === this._current;
        const goToLabel = btn.dataset.goToLabel;

        if (isActive) {
          btn.setAttribute('aria-pressed', this._paginationPaused ? 'true' : 'false');
          btn.setAttribute(
            'aria-label',
            this._paginationPaused ? this._resumeAutoplayLabel : this._pauseAutoplayLabel
          );
          return;
        }

        btn.removeAttribute('aria-pressed');
        if (goToLabel) {
          btn.setAttribute('aria-label', goToLabel);
        }
      });
    }

    _updateArrowState(index) {
      this._prevButtons.forEach((button) => {
        button.disabled = index <= 0;
      });
      this._nextButtons.forEach((button) => {
        button.disabled = index >= this._slides.length - 1;
      });
    }

    _announce(index) {
      if (!this._announcement) return;
      const slide = this._slides[index];
      if (!slide) return;
      const heading = slide.querySelector('.armada-teaser-content__heading');
      const headingText = heading ? heading.textContent.trim() : '';
      const indexText = String(index + 1);
      const totalText = String(this._slides.length);
      const slideLabel = this._slideLabelTemplate
        .replace('__INDEX__', indexText)
        .replace('__TOTAL__', totalText);

      if (headingText) {
        this._announcement.textContent = `${headingText}, ${slideLabel}`;
        return;
      }

      this._announcement.textContent = slideLabel;
    }

    /* ------------------------------------------------------------------
       Autoplay — only runs while section is in the viewport
       ------------------------------------------------------------------ */
    _startAutoplay() {
      this._stopAutoplay();
      if (!this._canAutoplay()) return;
      this._autoplayTimer = setTimeout(this._advance, this._duration * 1000);
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearTimeout(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    _restartAutoplay() {
      this._stopAutoplay();
      if (this._canAutoplay()) {
        this._autoplayTimer = setTimeout(this._advance, this._duration * 1000);
      }
    }

    _pauseAutoplay() {
      this._paused = true;
      this._stopAutoplay();
      this._setActiveFillAnimationState('paused');
    }

    _resumeAutoplay() {
      this._paused = false;
      this._setActiveFillAnimationState('running');
      this._restartAutoplay();
    }

    _setActiveFillAnimationState(state) {
      this._indicators.forEach((btn) => {
        if (!btn.classList.contains('is-active')) return;
        const fill = btn.querySelector('.armada-teaser-content__track-fill');
        if (!fill) return;
        fill.style.animationPlayState = state;
      });
    }

    /* ------------------------------------------------------------------
       Swipe (touch)
       ------------------------------------------------------------------ */
    _bindSwipe() {
      const SWIPE_THRESHOLD = 24;
      let startX = 0;
      let startY = 0;
      let deltaX = 0;
      let deltaY = 0;
      let tracking = false;
      let hasMoved = false;

      const handleSwipeEnd = () => {
        if (!tracking) return;
        const swipeX = deltaX;
        const swipeY = deltaY;
        const absX = Math.abs(swipeX);
        const absY = Math.abs(swipeY);
        tracking = false;
        deltaX = 0;
        deltaY = 0;
        hasMoved = false;
        if (this._mql.matches) return;
        if (absX < SWIPE_THRESHOLD || absX <= absY) return;
        this._navigateBy(swipeX < 0 ? 1 : -1);
      };
      this.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 1) return;
        ({ clientX: startX, clientY: startY } = e.touches[0]);
        deltaX = 0;
        deltaY = 0;
        hasMoved = false;
        tracking = true;
      }, { passive: true, capture: true });

      this.addEventListener('touchmove', (e) => {
        if (!tracking || e.touches.length !== 1) return;
        const { clientX, clientY } = e.touches[0];
        deltaX = clientX - startX;
        deltaY = clientY - startY;
        hasMoved = true;
      }, { passive: true, capture: true });

      this.addEventListener('touchend', (e) => {
        if (!tracking) return;
        if (!hasMoved) {
          tracking = false;
          return;
        }
        const touch = e.changedTouches[0];
        if (touch) {
          deltaX = touch.clientX - startX;
          deltaY = touch.clientY - startY;
        }
        handleSwipeEnd();
      }, { passive: true, capture: true });

      this.addEventListener('touchcancel', () => {
        tracking = false;
        deltaX = 0;
        deltaY = 0;
        hasMoved = false;
      }, { passive: true, capture: true });
    }

  }

  customElements.define('armada-teaser-content', ArmadaTeaserContent);
}
